import { Color as PersonaColor } from '@persona-id/design-tokens';
import Color from 'color';
import {
  BORDER_RADIUSES,
  FONT_SIZES,
  FONT_WEIGHTS,
  HEIGHTS,
  LINE_HEIGHTS,
  SPACINGS,
  TRANSITIONS,
} from 'shared/styles/variables';
import { css } from 'styled-components';

import FormControlProps from 'lib/interfaces/FormControlProps';
import ThemeSerialized from 'lib/interfaces/ThemeSerialized';
import { fontFamilyCss } from 'verify/lib/utils/font-family';

/**
 * We need this to be generic since we pass in arbitrary props here. So we
 * specify that they must contain FormControlProps, but we don't typecheck
 * the other contents.
 */
export interface LooseFormControlProps extends FormControlProps {
  [key: string]: any;
}

export const DISABLED_FIELD_BACKGROUND_COLOR = Color(PersonaColor.CoreSlate700)
  .fade(0.9)
  .rgb()
  .string();

/**
 * @deprecated Client-driven styles. Meant to eventually be fully replaced by server styles.
 */
export function fieldStyles(formControls: LooseFormControlProps, theme: Partial<ThemeSerialized>) {
  const colorPrimary = theme.colorPrimary;
  const borderRadius = theme.borderRadius ? theme.borderRadius : BORDER_RADIUSES.base;

  const matchNumeric = /(\d+)px/.exec(borderRadius);
  let cappedBorderRadius = borderRadius;
  if (matchNumeric) {
    // Cap at 4px
    if (parseInt(matchNumeric[1], 10) > 4) {
      cappedBorderRadius = BORDER_RADIUSES.rounded;
    }
  }

  return css`
    font-family: ${fontFamilyCss(theme.fontFamily)};

    border-radius: ${cappedBorderRadius};
    border-style: solid;
    border-width: 1px;
    box-sizing: border-box;
    display: block;
    min-width: 0;
    padding: ${SPACINGS.inputPadding};
    width: 100%;
    height: ${HEIGHTS.input};

    font-size: ${FONT_SIZES.input};
    font-weight: ${FONT_WEIGHTS.regular};
    line-height: ${LINE_HEIGHTS.input};
    outline: 0 !important;

    box-shadow: 0 0 0 0 ${PersonaColor.CorePurple900};
    transition: ${TRANSITIONS.fast};

    ${formControls.hasError
      ? css`
          border-color: ${theme.colorError};
          background-color: ${Color(theme.colorError).fade(0.97).rgb().string()};
          color: ${theme.colorError};

          &::placeholder {
            color: ${theme.colorError};
            opacity: 0.7;
          }
        `
      : formControls.hasWarning
        ? css`
            border-color: ${theme.colorWarning};
            background-color: ${Color(theme.colorWarning).fade(0.97).rgb().string()};
            color: ${theme.colorWarning};

            &::placeholder {
              color: ${theme.colorWarning};
              opacity: 0.7;
            }
          `
        : css`
            border-color: ${theme.colorInputBorder};
            background-color: ${theme.colorInputBackground};
            color: ${theme.colorFont ?? PersonaColor.CoreSlate1000};

            &::placeholder {
              color: ${theme.colorFont ?? PersonaColor.CoreSlate1000};
              opacity: 0.6;
            }
          `};

    &::placeholder {
      font-weight: ${FONT_WEIGHTS.regular};
    }

    &:focus-within {
      background-color: ${theme.colorInputBackground};
      border-color: ${colorPrimary};
      color: ${theme.colorFont ? theme.colorFont : PersonaColor.CoreSlate1000};

      /* Use box-shadow to ensure that inner content doesn't collapse (e.g. from using border-width. */
      box-shadow: 0 0 0 1px ${colorPrimary};

      &::placeholder {
        color: ${theme.colorFont ?? PersonaColor.CoreSlate1000};
        opacity: 0.6;
      }
    }

    &:disabled {
      background-color: ${DISABLED_FIELD_BACKGROUND_COLOR};
      border-color: ${PersonaColor.CoreSlate200};
      color: ${PersonaColor.CoreSlate900};
      // fix disabled text being white on Safari
      // this happens on screens after government ID step
      -webkit-text-fill-color: ${PersonaColor.CoreSlate900};

      &::placeholder {
        color: ${PersonaColor.CoreSlate800};
        -webkit-text-fill-color: ${PersonaColor.CoreSlate800};
        opacity: 1;
      }
    }
  `;
}
